<template>
  <div class="about-us w-100 text-center mt-5 mb-5">
    <div class="about-citsci mx-auto mb-5 text-left">
      <div class="content p-4">
        <h3 class="text-grey-800">We are a global citizen science support platform. We streamline citizen
          science so you can focus on designing and implementing projects for
          greater impact.</h3>
        <p class="color-gray">On CitSci, thousands of people engage in science through projects
          created by folks just like you. New projects are created all the time.
          Many gain insight and inspiration from your work and others. Have an idea
          for a new citizen science project? At CitSci, you’ll find tools to
          support your entire research process, from creating projects to managing
          participants; building custom data sheets; collecting data; sharing and
          analyzing data; and gathering feedback. We don't just host citizen science
          projects; we transform citizen science into action.</p>
      </div>
    </div>

    <div class="about-stats bg-cit-green d-flex flex-column justify-content-center py-5">
      <div class="content">
        <div class="column text-white d-inline-block align-self-center">
          <div class="num">2007</div>
          <div class="data-name">Founded</div>
          <hr />
        </div>

        <div class="column text-white d-inline-block align-self-center">
          <div class="num">{{ stats.usersTotal }}</div>
          <div class="data-name">People</div>
          <hr />
        </div>

        <div class="column text-white d-inline-block align-self-center">
          <div class="num">{{ stats.projectsTotal }}</div>
          <div class="data-name">Projects</div>
          <hr />
        </div>

        <div class="column text-white d-inline-block align-self-center">
          <div class="num">{{ stats.measurementsTotal }}</div>
          <div class="data-name">Measurements</div>
          <hr class="two-columns" />
        </div>
        <div class="column text-white d-inline-block align-self-center">
          <div class="num">{{ stats.observationsTotal }}</div>
          <div class="data-name">Observations</div>
          <hr class="one-columns" />
        </div>
        <div class="column text-white d-inline-block align-self-center">
          <div class="num">{{ stats.locationsTotal }}</div>
          <div class="data-name">Locations</div>
        </div>
      </div>
    </div>

    <div class="how-it-works m-5 d-flex flex-column text-left">
      <h1>How It Works</h1>

      <div class="row d-flex flex-row align-items-center justify-content-center w-100 mt-3 mx-auto">
        <div class="column"></div>
        <div class="column">
          <font-awesome-icon icon="chevron-down" size="3x" class="mx-auto" />
        </div>
        <div class="column"></div>
      </div>
      <div class="row d-flex flex-row align-items-center justify-content-center w-100 mt-3 mx-auto">
        <div class="column">
          <h1 class="mb-3 mt-4 text-center text-3xl font-normal">Create projects</h1>
          <p class="color-gray">
            Tell us a little bit about your project and you’ll be up and running on CitSci in no time! Plus, with one
            click,
            you can automatically share your project with SciStarter to help recruit participants.
          </p>
        </div>
        <div class="column">
          <div class="circle-icon border-primary">
            <font-awesome-icon :icon="['far', 'lightbulb']" size="3x" class="mx-auto text-primary" />
          </div>
        </div>
        <div class="column">

        </div>
      </div>
      <div class="row d-flex flex-row align-items-center justify-content-center w-100 mt-3 mx-auto">
        <div class="column">
        </div>
        <div class="column">
          <div class="circle-icon border-tertiary text-tertiary">
            <font-awesome-icon :icon="['far', 'edit']" size="3x" class="mx-auto" />
          </div>
        </div>
        <div class="column">
          <h1 class="mb-3 mt-4 text-center text-3xl font-normal">Collect data</h1>
          <p class="color-gray">Create customized online datasheets using our new and improved Datasheet Creator. The datasheets are made
            available to the volunteers in your project for print, web, or mobile use. </p>
        </div>
      </div>

      <div class="row d-flex flex-row align-items-center justify-content-center w-100 mt-3 mx-auto">
        <div class="column">
          <h1 class="mb-3 mt-4 text-center text-3xl font-normal">View dynamic charts and graphs</h1>
          <p class="color-gray">Visualize data in maps, view trends in data over time, compare groups of data, or browse basic summary
            statistics.</p>
        </div>
        <div class="column">
          <div class="circle-icon border-orange text-orange">
            <font-awesome-icon icon="chart-line" size="3x" class="mx-auto" />
          </div>
        </div>
        <div class="column">
        </div>
      </div>

      <div class="row d-flex flex-row align-items-center justify-content-center w-100 mt-3 mx-auto">
        <div class="column">
        </div>
        <div class="column">
          <div class="circle-icon color-gray border-grey-200">
            <font-awesome-icon icon="comments" size="3x" class="mx-auto" />
          </div>
        </div>
        <div class="column">
          <h1 class="mt-4 text-center text-3xl font-normal">Export and share datasets</h1>
          <p class="color-gray">Export, download, and share your data as an Excel spreadsheet or CSV file.</p>
        </div>
      </div>

      <div class="row d-flex flex-row align-items-center justify-content-center w-100 mt-3 mx-auto">
        <div class="column"></div>
        <div class="column">
          <font-awesome-icon icon="chevron-down" size="3x" class="mx-auto" />
        </div>
        <div class="column"></div>
      </div>
    </div>

    <div class="discover">
      <h1 class="mt-4 text-center text-4xl font-normal">Discover Results and Amplify Impacts</h1>
      <p class="color-gray">
        Whether building your own datasheets or collecting field data on your mobile devices, we streamline the research
        process making it easy to go from the field to dynamic real-time charts and analyses instantly.
      </p>
    </div>

    <div class="our-story py-4 px-3 px-lg-0" style="background-color: #F0F2DF;">
      <h1 class="py-2 mx-auto" style="max-width: 1030px;">Our Story</h1>
      <p class="py-1 text-left text-secondary mx-auto" style="max-width: 1030px;">
        In 2007, a group of researchers were looking for a way to include more people in a scientific research project
        studying invasive plants in national forests. What started as a single project quickly turned into a platform
        for
        building and hosting citizen science projects from around the world. With the support of the Natural Resource
        Ecology
        Lab (<a href="https://www.nrel.colostate.edu/" target="NewWindow">NREL</a>) at <a
          href="https://www.colostate.edu/" target="NewWindow">
          Colorado State University</a>,
        CitSci now hosts {{ stats.projectsTotal }} projects that have contributed more than
        {{ stats.measurementsTotal }} data points to answer local, regional, and global scientific questions.
      </p>
      <p class="py-1 text-left text-secondary mx-auto" style="max-width: 1030px;">
        Our team consists of project managers, programmers, scientists, educators, and citizen scientists. Each team
        member
        has developed scientific research projects, collected and analyzed data, and worked with volunteers and
        stakeholders.
        We work every day to make your citizen science experience a breeze.
      </p>
      <span class="btn btn-primary py-1 text-center mx-auto" style="max-width: 1030px;">
        <b-link :to="{ name: 'Staff' }" class="text-2xl text-white">Meet our team</b-link>
      </span>
    </div>

    <div class="our-values bg-primary py-5">
      <h1 class="text-white mb-5">
        Our Values
      </h1>
      <p class="p-up w-75 mb-6 text-center mx-auto text-white">
        We recognize an inclusive definition of citizen science. We are excited to work with
        individuals interested in their own investigations as well as community-based, collaborative,
        or scientist-driven projects.
      </p>
      <ul class="w-75 text-grey-800 list-unstyled text-left mx-auto" style="max-width: 760px;">
        <li class="text-3xl py-2 d-flex">
          <span class="mr-3"><font-awesome-icon icon="check-circle" size="lg" class="mx-auto" /></span>
          <span class="text-3xl">We value people and the science they conduct</span>
        </li>
        <li class="text-3xl py-2 d-flex">
          <span class="mr-3"><font-awesome-icon icon="check-circle" size="lg" class="mx-auto" /></span>
          <span class="text-3xl">We value comprehensive support from concept to close</span>
        </li>
        <li class="text-3xl py-2 d-flex">
          <span class="mr-3"><font-awesome-icon icon="check-circle" size="lg" class="mx-auto" /></span>
          <span class="text-3xl">We value collaboration and co-creation</span>
        </li>
      </ul>
    </div>

    <div class="all-about-results m-5 d-flex flex-column">
      <h1>
        We're All About Results
      </h1>
      <div class="row">
        <div class="col my-3">
          <div class="icon mx-auto mb-4" style="width: 74px; height: 74px;">
            <b-link href="https://blog.citsci.org/2018/07/02/trout-unlimited/" target="NewWindow">
              <img src="/assets/images/about/wather-drop.png" class="h-100 w-auto" style="max-width: 74px;" /></b-link>
          </div>
          <div class="text text-3xl text-secondary">
            Water quality parameters collected to study
            <b-link href="https://blog.citsci.org/2018/07/02/trout-unlimited/" target="NewWindow">
              long-term health of streams</b-link>
          </div>
        </div>
        <div class="col my-3">
          <div class="icon mx-auto mb-4" style="width: 74px; height: 74px;">
            <b-link href="https://blog.citsci.org/2017/10/24/the-front-range-pika-project-why-pikas-part-1/"
              target="NewWindow"><img src="/assets/images/about/pika-icon.png" class="h-100" style="max-width: 74px; width: auto;" /></b-link>
          </div>
          <div class="text text-3xl text-secondary">
            American pika populations monitored to determine
            <b-link href="https://blog.citsci.org/2017/10/24/the-front-range-pika-project-why-pikas-part-1/"
              target="NewWindow">risks of local extirpations</b-link>
          </div>
        </div>
        <div class="col my-3">
          <div class="icon mx-auto mb-4" style="width: 74px; height: 74px;">
            <b-link
              href="https://blog.citsci.org/2021/07/19/young-kenyans-aim-to-establish-sustainable-bamboo-agroecological-farming-with-the-help-of-citizen-science/"
              target="NewWindow">
              <img src="/assets/images/about/leaf.png" class="h-100" style="max-width: 74px; width: auto;" /></b-link>
          </div>
          <div class="text text-3xl text-secondary">
            Developing a local bamboo agroecology to <b-link
              href="https://blog.citsci.org/2021/07/19/young-kenyans-aim-to-establish-sustainable-bamboo-agroecological-farming-with-the-help-of-citizen-science/"
              target="NewWindow">create a sustainable community income</b-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script type="text/javascript">
import { mapActions, mapGetters } from 'vuex'

export default {
  components: {},
  created() {
    this.sendDataToHeader(this.headerData, this)
    this.getStats();
  },
  beforeDestroy() {
    this.reset()
  },

  computed: mapGetters({
    stats: 'about/show/stats'
  }),

  methods: {
    ...mapActions({
      getStats: 'about/show/getStats',
    }),

    /**
     * @function reset - reset the object to the default values
     */
    reset() {
      // TODO reset the object to the default values
      // console.log("Reset object")
    }
  },

  /**
   *
   * @returns {{headerData: {backgroundPath: string, picturePath: string, headerTitle: string}}}
   */
  data() {
    return {
      headerData: {
        backgroundPath: '/assets/images/header/about-us-header.png',
        picturePath: '',
        headerTitle: 'About Us'
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.circle-icon {
  width: 80px;
  height: 80px;
}

.about-us {
  font-size: 18px;

  &>.about-citsci {
    max-width: 1560px;

    @media screen and (max-width: 1000px) {
      margin: 0 30px 45px;
    }

    &>.content {
      max-width: 941px;
      margin: 0 auto;
    }

    h3 {
      font-size: 26px;
      color: #666666;
      margin: 0 auto 30px auto;
      max-width: 758px;
    }
  }

  &>.about-stats {
    .content {
      max-width: 1000px;
      margin: 0 auto;

      @media screen and (max-width: 923px) {
        margin: 80px auto;
      }

      .column {
        width: 300px;

        &>.num {
          font-size: 42px;
          font-family: "Times New Roman";
        }

        &>.data-name {
          font-size: 20px;
        }

        &>hr {
          display: block;
          height: 1px;
          border: 0;
          border-top: 1px solid #fff;
          margin: 30px 17px;
          padding: 0;
        }

        &>hr.two-columns {
          display: none;

          @media screen and (max-width: 923px) {
            display: block;
          }
        }

        &>hr.one-columns {
          display: none;

          @media screen and (max-width: 619px) {
            display: block;
          }
        }
      }
    }

  }

  &>.how-it-works {

    @media screen and (max-width: 700px) {
      padding: 0 30px;
    }

    &>.row {

      @media screen and (max-width: 700px) {
        flex-wrap: nowrap;
      }

      &>.column {
        width: 400px;
        flex-direction: column;
        margin: 0 10px;

        @media screen and (max-width: 950px) {
          flex-wrap: nowrap;
          width: auto;
          max-width: 400px;
        }
      }

      &>.column:nth-child(2) {
        width: 65px;
        text-align: center;
      }
    }
  }

  &>.discover {
    max-width: 710px;
    justify-content: center;
    margin: 0 auto;
    min-height: 200px;

    @media screen and (max-width: 700px) {
      padding: 0 30px;
    }

    &>h1 {
      font-size: 38px;
      margin-bottom: 24px;
    }

    &>p {
      text-align: left;
      margin: 0 auto;
      max-width: 650px;
      color: #aaaaaa;
    }
  }
}
</style>
